import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getSystemUiModuleList(
    $filter: systemUiModuleFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getSystemUiModuleList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        uiModuleComponentList {
          id
          name
          apiModuleList {
            id
            name
          }
          roleList {
            id
            name
          }
        }
        roleList {
          id
          name
        }
        name
        id
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.SystemUiModuleListState = {
  result: null,
  error: null,
  loading: false
};

export const getSystemUiModuleListRequest = createAsyncThunk<
  TYPES.SystemUiModuleList,
  {
    filter?: TYPES.SystemUiModuleFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'system_ui_module/get_system_ui_module_list',
  async (
  params: {
    filter?: TYPES.SystemUiModuleFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getSystemUiModuleList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getSystemUiModuleListSlice = createSlice({
  name: 'get_system_ui_module_list',
  initialState: initialState,
  reducers: {
    getSystemUiModuleListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getSystemUiModuleListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getSystemUiModuleListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getSystemUiModuleListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getSystemUiModuleListClear } =
getSystemUiModuleListSlice.actions;

export const getSystemUiModuleList = createSelector(
  (state: RootState) => state.systemUiModule.getSystemUiModuleList.result,
  (state: RootState) => state.systemUiModule.getSystemUiModuleList.error,
  (state: RootState) =>
  state.systemUiModule.getSystemUiModuleList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getSystemUiModuleListSlice.reducer;